import { useGetCreditApplicationById } from '@src/api/credit-api'
import useGetSkipBankRequestRequirements from '@src/api/skip-bank-request-api'
import { useRequiredDocumentsWithStatus } from '@src/data/credit-application-hooks'
import { scrollToTop } from '@src/services/utils'
import { useEffect, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import Loader from '../../components/Loader'

import { canPollCreditApp, computeHighLevelPage, isServerAnalysingCredit } from './credit-hooks'

import ErrorPage from '../ErrorPage'
import ClosedApplicationTile from './components/ClosedApplicationTile'
import DecisionningErrorTile from './components/DecisionningErrorTile'

import CompletedAppPage from './CompletedApp/CompletedAppPage'
import HardHitApprovedResultPage from './HardHitApprovedResultPage'
import HardHitUnapprovedResultPage from './HardHitUnapprovedResultPage'
import InitialApprobationForm from './InitialApprobationPage/InitialApprobationForm'
import PrequalificationResultPage from './PrequalResultPage'
import HardHitConsentNoCreditReportPage from './components/HardHitConsentNoCreditReportPage'
import AgentContactPage from '../CancelApplication/AgentContactPage'

const RouteToStatePage = () => {
  const { t } = useTranslation()
  const allParams = useParams()
  const id = allParams.id!
  const [shouldPoll, setShouldPoll] = useState(false)
  const [creditApp, isFetchingCreditApp] = useGetCreditApplicationById(id, shouldPoll) // if true, poll every 5 secs
  const [skipBankRequiredDocuments, isFetchingReqDocsForSkip] = useGetSkipBankRequestRequirements(id)
  const skipBankRequiredDocumentsWithStatus = useRequiredDocumentsWithStatus(
    skipBankRequiredDocuments,
    creditApp?.documents,
  )
  const highLevelPage = computeHighLevelPage(creditApp, isFetchingCreditApp)

  useEffect(() => {
    if (creditApp) {
      const should = isServerAnalysingCredit(creditApp) && canPollCreditApp(creditApp)
      setShouldPoll(should)
    }
  }, [creditApp])

  useLayoutEffect(scrollToTop, [])

  if (isFetchingReqDocsForSkip || isFetchingCreditApp || highLevelPage === 'loading') {
    return (
      <div>
        <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />
      </div>
    )
  }

  return (
    <>
      {highLevelPage === 'notFound' && <ErrorPage title={t('common.notFound')} />}

      {highLevelPage === 'analysis-running' && (
        <div>
          <Loader
            title={t('finalDecision.processing')}
            description={`${t('common.pleaseWait') as string} ${t('common.withYouShortly') as string}`}
          />
        </div>
      )}

      {highLevelPage === 'cancelling' && <AgentContactPage />}

      {highLevelPage === 'prequal-result' && <PrequalificationResultPage creditApp={creditApp!} />}

      {highLevelPage === 'decisionning-error' && <DecisionningErrorTile />}

      {highLevelPage === 'first-approval' && <InitialApprobationForm creditApp={creditApp!} />}

      {highLevelPage === 'hardhit-result' && <HardHitUnapprovedResultPage creditApp={creditApp!} />}

      {highLevelPage === 'approved' && (
        <HardHitApprovedResultPage
          creditApp={creditApp!}
          skipBankAccountRequiredDocuments={skipBankRequiredDocumentsWithStatus}
        />
      )}
      {highLevelPage === 'hardHit-NoCreditReport' && <HardHitConsentNoCreditReportPage creditApp={creditApp!} />}
      {highLevelPage === 'closed' && <ClosedApplicationTile creditApp={creditApp!} />}

      {highLevelPage === 'funding' && <CompletedAppPage creditApp={creditApp!} />}
    </>
  )
}

export default RouteToStatePage
